import { useState, useEffect, useMemo, useRef } from 'react';
import * as Clipboard from 'expo-clipboard';
import { View, Text, ScrollView, TouchableOpacity, Modal, TextInput, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Crown,
  Shield,
  Sparkles,
  Copy,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Bell,
} from 'lucide-react-native';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { AppSelect } from '@/components/ui/AppSelect';
import { Avatar } from './ui/Avatar';
import { Textarea } from './ui/Textarea';
import { Input } from './ui/Input';

// Hooks and Utils
import { useToast } from '../hooks/useToast';
import { apiRequestJson } from '../lib/queryClient';
import { useTRPC, useTRPCClient } from '../lib/trpc';
import { clientLogger } from '../lib/datadog';
import { notificationPresetOptions } from '@/lib/notificationPresets';
import { config } from '@/config/env';
import { colors, typography } from '@/lib/theme';
import { PICK_LOCKED_ERROR } from '@shared/pickLocking';

const modalOverlayColor = 'rgba(0, 0, 0, 0.5)';
const loadingContentStyle = { padding: 24 };

interface AdminPicksManagementProps {
  leagueId: string;
  selectedWeekId: string;
  seasonId?: string | null;
}

interface LeagueMember {
  league_members: {
    id: string;
    userId: string;
    buyInStatus: string;
    isManager: boolean;
  };
  users: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

// Types corresponding to LeagueScreen definitions
type LeagueGameBet = {
  id: string;
  leagueWeekBetId?: string | null;
  betType: 'moneyline' | 'spread' | 'total' | 'total_tiebreaker' | 'custom_prop';
  oddsOne?: number | null;
  oddsTwo?: number | null;
  displayInfo?: Record<string, unknown> | null;
  cutoffAt?: string | null;
  pickLockCutoffAt?: string | null;
  locked?: boolean;
  scoringConfigOverride?: Record<string, unknown> | null;
};

type LeagueWeekGameGroup = {
  game: any;
  bets: LeagueGameBet[];
};

type LeagueWeekGamesPayload = {
  leagueWeekId: string | null;
  resolvedSeasonId: string;
  legacy?: boolean;
  games: LeagueWeekGameGroup[];
};

type LeagueSeasonWeekOption = {
  id: string;
  leagueWeekId: string;
  label: string;
  displayLabel: string;
  weekNumber: number | null;
  startsAt: string | null;
  endsAt: string | null;
};

type LeagueSeasonWeeksPayload = {
  resolvedSeasonId: string;
  weeks: LeagueSeasonWeekOption[];
};

const formatOdds = (odds?: number | string | null) => {
  if (odds === undefined || odds === null) return 'EVEN';
  const parsed = typeof odds === 'string' ? parseFloat(odds) : odds;
  if (!Number.isFinite(parsed)) return 'EVEN';
  return parsed > 0 ? `+${parsed}` : `${parsed}`;
};

const formatSpread = (value?: number | null) => {
  if (value === undefined || value === null) return '';
  if (value > 0) return `+${value}`;
  return `${value}`;
};

const formatTotal = (value?: number | null) => {
  if (value === undefined || value === null) return '';
  return `${value}`;
};

const buildBetLabel = (bet: LeagueGameBet) => {
  if (bet.betType === 'moneyline' && bet.displayInfo?.marketSubtype === 'soccer_to_advance') {
    return 'To Advance';
  }
  switch (bet.betType) {
    case 'spread':
      return 'Spread';
    case 'total':
      return 'Total';
    case 'total_tiebreaker':
      return 'Tiebreaker';
    case 'custom_prop': {
      if (bet.displayInfo?.propSubtype === 'soccer_regulation_moneyline') {
        return 'Regulation Moneyline';
      }
      const label = typeof bet.displayInfo?.label === 'string' ? bet.displayInfo.label.trim() : '';
      return label || 'Custom Prop';
    }
    case 'moneyline':
    default:
      return 'Moneyline';
  }
};

export default function AdminPicksManagement({
  leagueId,
  selectedWeekId,
  seasonId,
}: AdminPicksManagementProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const adminTimeoutMs = config.adminApiTimeoutMs;

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [selectedBetId, setSelectedBetId] = useState<string>(''); // Identifying the specific bet (leagueWeekBetId or constructed ID)
  const [selectedSideValue, setSelectedSideValue] = useState<string>(''); // Could be teamId or side key
  const [tiebreakerGuessInput, setTiebreakerGuessInput] = useState<string>('');

  const [recapPrompt, setRecapPrompt] = useState<string>('');
  const [recapDialogOpen, setRecapDialogOpen] = useState(false);
  const [recapCopied, setRecapCopied] = useState(false);
  const [recapType, setRecapType] = useState<'season' | 'weekly'>('weekly');
  const [recapTone, setRecapTone] = useState<'fun' | 'competitive' | 'chaotic' | 'professional'>('fun');
  const [recapWeekId, setRecapWeekId] = useState<string>(selectedWeekId || '');

  const [showMissingPicks, setShowMissingPicks] = useState(true);
  const [notificationTarget, setNotificationTarget] = useState<'all' | 'missing'>('all');
  const [notificationPreset, setNotificationPreset] = useState(notificationPresetOptions[0]!.value);
  const [notificationMessage, setNotificationMessage] = useState(notificationPresetOptions[0]!.message);
  const notificationMessageLimit = 200;
  const [notificationTitle, setNotificationTitle] = useState(notificationPresetOptions[0]!.title);
  const notificationTitleLimit = 80;
  const [overrideSquareUserId, setOverrideSquareUserId] = useState<string>('');
  const [overrideSquareRow, setOverrideSquareRow] = useState<string>('1');
  const [overrideSquareCol, setOverrideSquareCol] = useState<string>('1');

  // Fetch all league members for the user dropdown
  const { data: members = [], isLoading: membersLoading } = useQuery<LeagueMember[]>(
    trpc.leagues.members.queryOptions(
      { leagueId, seasonId: seasonId ?? undefined },
      { enabled: !!seasonId },
    ) as any,
  );

  // Fetch games AND bets for the week (same as LeagueScreen)
  const { data: leagueWeekGames, isLoading: gamesLoading } = useQuery<LeagueWeekGamesPayload | null>({
    queryKey: ['/api/leagues', leagueId, 'weeks', selectedWeekId, seasonId ?? 'none'],
    enabled: !!selectedWeekId && !!leagueId && !!seasonId,
    queryFn: async () => {
      if (!selectedWeekId || !seasonId) {
        return null;
      }
      const params = new URLSearchParams();
      params.set('seasonId', seasonId);
      return await apiRequestJson<LeagueWeekGamesPayload>(
        `/api/leagues/${leagueId}/weeks/${selectedWeekId}/games?${params.toString()}`,
        'GET',
        undefined,
        { timeoutMs: adminTimeoutMs },
      );
    },
  });

  // Fetch users without complete picks (for the list view)
  const usersWithoutPicksInput = {
    leagueId,
    weekId: selectedWeekId ?? '',
    ...(seasonId ? { seasonId } : {}),
  };
  const { data: usersWithoutPicks } = useQuery(
    trpc.picks.usersWithoutPicks.queryOptions(usersWithoutPicksInput, {
      enabled: !!selectedWeekId && !!seasonId,
    }),
  );

  // Fetch current week for default recap week
  const { data: currentWeek } = useQuery(
    trpc.nfl.currentWeek.queryOptions(undefined, {
      staleTime: 60_000,
    }),
  );

  const { data: seasonWeeks } = useQuery<LeagueSeasonWeeksPayload | null>({
    queryKey: ['/api/leagues', leagueId, 'season-weeks', seasonId ?? 'none', 'recap'],
    enabled: !!leagueId && !!seasonId,
    queryFn: async () => {
      if (!leagueId || !seasonId) {
        return null;
      }
      const params = new URLSearchParams();
      params.set('seasonId', seasonId);
      return apiRequestJson<LeagueSeasonWeeksPayload>(
        `/api/leagues/${leagueId}/weeks?${params.toString()}`,
        'GET',
        undefined,
        { timeoutMs: adminTimeoutMs },
      );
    },
  });

  const squaresInput = useMemo(
    () => ({
      leagueId,
      ...(seasonId ? { seasonId } : {}),
    }),
    [leagueId, seasonId],
  );

  const { data: squaresState } = useQuery(
    trpc.squares.state.queryOptions(squaresInput, {
      enabled: !!leagueId && !!seasonId,
    }),
  );

  const seasonWeekOptions = useMemo(() => seasonWeeks?.weeks ?? [], [seasonWeeks]);
  const missingPicksCount = usersWithoutPicks?.length ?? 0;
  const notificationRecipientOptions = useMemo(
    () => [
      { value: 'all', label: 'All league members' },
      { value: 'missing', label: `Missing picks (${missingPicksCount})` },
    ],
    [missingPicksCount],
  );
  const kickoffTime = useMemo(() => {
    const games = leagueWeekGames?.games ?? [];
    if (!games.length) return null;
    return games.reduce<Date | null>((current, game) => {
      const gameTime = game?.game?.gameTime ? new Date(game.game.gameTime) : null;
      if (!gameTime || isNaN(gameTime.getTime())) return current;
      if (!current || gameTime < current) return gameTime;
      return current;
    }, null);
  }, [leagueWeekGames?.games]);
  const missingSquaresCount = useMemo(() => {
    if (!squaresState?.enabled) return 0;
    const assignedUserIds = new Set((squaresState.entries ?? []).map((entry) => entry.userId));
    return members.reduce((total, member) => {
      const memberUserId = member?.users?.id;
      if (!memberUserId) return total;
      return assignedUserIds.has(memberUserId) ? total : total + 1;
    }, 0);
  }, [members, squaresState]);
  const squareCoordinateOptions = useMemo(
    () => Array.from({ length: 10 }, (_, index) => ({ value: String(index + 1), label: String(index + 1) })),
    [],
  );
  const squareOverrideMemberOptions = useMemo(
    () =>
      members
        .map((member) => {
          const userId = member?.users?.id;
          if (!userId) return null;
          const firstName = member.users.firstName?.trim() ?? '';
          const lastName = member.users.lastName?.trim() ?? '';
          const displayName = `${firstName} ${lastName}`.trim() || member.users.email || 'Unknown member';
          return { value: userId, label: displayName };
        })
        .filter((entry): entry is { value: string; label: string } => Boolean(entry)),
    [members],
  );
  useEffect(() => {
    if (!overrideSquareUserId && squareOverrideMemberOptions.length > 0) {
      setOverrideSquareUserId(squareOverrideMemberOptions[0]!.value);
      return;
    }
    if (
      overrideSquareUserId &&
      squareOverrideMemberOptions.length > 0 &&
      !squareOverrideMemberOptions.some((option) => option.value === overrideSquareUserId)
    ) {
      setOverrideSquareUserId(squareOverrideMemberOptions[0]!.value);
    }
  }, [overrideSquareUserId, squareOverrideMemberOptions]);
  const selectedOverrideRow = Number(overrideSquareRow);
  const selectedOverrideCol = Number(overrideSquareCol);
  const selectedOverrideCellKey =
    Number.isInteger(selectedOverrideRow) && Number.isInteger(selectedOverrideCol)
      ? `${selectedOverrideRow - 1}-${selectedOverrideCol - 1}`
      : null;
  const selectedSquareOwner = useMemo(() => {
    if (!selectedOverrideCellKey || !squaresState?.entries?.length) {
      return null;
    }
    const entry = squaresState.entries.find(
      (squareEntry) => `${squareEntry.rowIndex}-${squareEntry.colIndex}` === selectedOverrideCellKey,
    );
    if (!entry) return null;
    const member = members.find((memberEntry) => memberEntry.users.id === entry.userId);
    if (!member) {
      return entry.displayName || 'Unknown member';
    }
    const firstName = member.users.firstName?.trim() ?? '';
    const lastName = member.users.lastName?.trim() ?? '';
    return `${firstName} ${lastName}`.trim() || member.users.email || 'Unknown member';
  }, [members, selectedOverrideCellKey, squaresState?.entries]);
  const autoAssignSquaresMutation = useMutation({
    mutationFn: async () => trpcClient.squares.autoAssign.mutate(squaresInput),
    onSuccess: async (payload: any) => {
      await queryClient.invalidateQueries(trpc.squares.state.queryFilter(squaresInput));
      const assignedCount = typeof payload?.assignedCount === 'number' ? payload.assignedCount : 0;
      toast.show({
        type: 'success',
        text1: 'Squares auto-assigned',
        text2:
          assignedCount > 0
            ? `Assigned ${assignedCount} random square${assignedCount === 1 ? '' : 's'}.`
            : 'Everyone already has a square pick.',
      });
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        text1: 'Auto-assign failed',
        text2: error?.message || 'Unable to auto-assign squares.',
      });
    },
  });
  const overrideSquareMutation = useMutation({
    mutationFn: async () => {
      if (!overrideSquareUserId) {
        throw new Error('Select a member to assign this square to.');
      }
      const rowIndex = Number(overrideSquareRow) - 1;
      const colIndex = Number(overrideSquareCol) - 1;
      if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex > 9) {
        throw new Error('Select a valid row.');
      }
      if (!Number.isInteger(colIndex) || colIndex < 0 || colIndex > 9) {
        throw new Error('Select a valid column.');
      }

      return trpcClient.squares.override.mutate({
        ...squaresInput,
        userId: overrideSquareUserId,
        rowIndex,
        colIndex,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(trpc.squares.state.queryFilter(squaresInput));
      const selectedMember = squareOverrideMemberOptions.find((option) => option.value === overrideSquareUserId);
      toast.show({
        type: 'success',
        text1: 'Square overridden',
        text2: `${selectedMember?.label ?? 'Member'} now owns row ${overrideSquareRow}, column ${overrideSquareCol}.`,
      });
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        text1: 'Override failed',
        text2: error?.message || 'Unable to override square.',
      });
    },
  });

  const formatRelativeKickoff = (date: Date) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const startOfDayAfterTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);

    if (date >= startOfToday && date < startOfTomorrow) {
      return `later today`;
    }
    if (date >= startOfTomorrow && date < startOfDayAfterTomorrow) {
      return `tomorrow`;
    }
    return `soon`;
  };

  const lastCallMessage = useMemo(() => {
    if (!kickoffTime) {
      return 'Last call! Picks lock soon for this week.';
    }
    return `Picks lock ${formatRelativeKickoff(kickoffTime)}, be sure to get your picks in soon!`;
  }, [kickoffTime]);
  const lastCallMessageRef = useRef(lastCallMessage);

  // Fetch user picks when a user is selected
  const { data: selectedUserPicks, isLoading: userPicksLoading } = useQuery(
    trpc.picks.userPicks.queryOptions(
      {
        leagueId,
        userId: selectedUserId,
        weekId: selectedWeekId ?? '',
        ...(seasonId ? { seasonId } : {}),
      },
      { enabled: !!selectedUserId && !!selectedWeekId && !!seasonId },
    ),
  );

  // Helper to build bet key
  const buildBetKey = (gameId: string, bet: LeagueGameBet) => {
    if (bet.leagueWeekBetId) return bet.leagueWeekBetId;
    if (bet.id) return bet.id;
    return `${gameId}-${bet.betType}`;
  };

  const handleNotificationPresetChange = (value: string) => {
    setNotificationPreset(value);
    if (value === 'last-call') {
      setNotificationMessage(lastCallMessage);
      setNotificationTitle(notificationPresetOptions.find((option) => option.value === value)?.title ?? 'League Update');
      return;
    }
    const preset = notificationPresetOptions.find((option) => option.value === value);
    if (preset) {
      setNotificationMessage(preset.message);
    }
    if (preset?.title) {
      setNotificationTitle(preset.title);
    }
  };

  useEffect(() => {
    const previousMessage = lastCallMessageRef.current;
    if (notificationPreset === 'last-call' && notificationMessage === previousMessage) {
      setNotificationMessage(lastCallMessage);
    }
    lastCallMessageRef.current = lastCallMessage;
  }, [lastCallMessage, notificationMessage, notificationPreset]);

  const sendNotificationMutation = useMutation({
    mutationFn: async () => {
      const trimmedMessage = notificationMessage.trim();
      const trimmedTitle = notificationTitle.trim() || 'League Update';

      return trpcClient.picks.notifyLeague.mutate({
        leagueId,
        target: notificationTarget,
        message: trimmedMessage,
        title: trimmedTitle,
        ...(notificationTarget === 'missing' && selectedWeekId ? { weekId: selectedWeekId } : {}),
        ...(seasonId ? { seasonId } : {}),
      });
    },
    onSuccess: (data) => {
      toast.show({
        type: 'success',
        text1: 'Notifications sent',
        text2: data?.message ?? 'Your league members have been notified.',
      });
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        text1: 'Unable to send notifications',
        text2: error?.message ?? 'Please try again.',
      });
    },
  });

  useEffect(() => {
    if (!seasonWeekOptions.length) return;
    if (recapWeekId && seasonWeekOptions.some((week) => week.id === recapWeekId)) {
      return;
    }
    const currentWeekNumber = currentWeek?.week;
    const fallbackWeek =
      (typeof currentWeekNumber === 'number'
        ? seasonWeekOptions.find((week) => week.weekNumber === currentWeekNumber)
        : null) ??
      (selectedWeekId ? seasonWeekOptions.find((week) => week.id === selectedWeekId) : null) ??
      seasonWeekOptions[0];
    if (fallbackWeek) {
      setRecapWeekId(fallbackWeek.id);
    }
  }, [seasonWeekOptions, recapWeekId, currentWeek?.week, selectedWeekId]);

  // Derived state for the form
  const selectedGameGroup = useMemo(() => {
    return leagueWeekGames?.games.find((g) => g.game.id === selectedGameId);
  }, [leagueWeekGames, selectedGameId]);

  const availableBets = useMemo(() => {
    return selectedGameGroup?.bets ?? [];
  }, [selectedGameGroup]);

  const selectedBet = useMemo(() => {
    if (!selectedGameGroup) return null;
    return availableBets.find((bet) => buildBetKey(selectedGameGroup.game.id, bet) === selectedBetId);
  }, [availableBets, selectedBetId, selectedGameGroup]);

  useEffect(() => {
    if (selectedBet?.betType === 'total_tiebreaker') {
      setSelectedSideValue('tiebreaker');
    } else if (selectedSideValue === 'tiebreaker') {
      setSelectedSideValue('');
    }
  }, [selectedBet?.betType, selectedSideValue]);

  // Determine side options for the selected bet
  const sideOptions = useMemo(() => {
    if (!selectedBet || !selectedGameGroup) return [];

    const { game } = selectedGameGroup;
    const { betType, displayInfo, oddsOne, oddsTwo } = selectedBet;

    if (betType === 'total_tiebreaker') {
      return [
        {
          value: 'tiebreaker',
          label: 'Tiebreaker guess',
          side: 'one',
          teamId: game.homeTeam.id,
        },
      ];
    }

    if (betType === 'total') {
      const totalValue = typeof displayInfo?.total === 'number' ? displayInfo.total : null;
      return [
        {
          value: game.homeTeam.id, // Using home team ID for 'Over' / 'Side 1' (legacy convention) or construct specific value
          label: `Over ${formatTotal(totalValue)} (${formatOdds(oddsOne)})`,
          side: 'one',
          teamId: game.homeTeam.id,
        },
        {
          value: game.awayTeam.id, // Using away team ID for 'Under' / 'Side 2'
          label: `Under ${formatTotal(totalValue)} (${formatOdds(oddsTwo)})`,
          side: 'two',
          teamId: game.awayTeam.id,
        },
      ];
    }

    if (betType === 'custom_prop') {
      if (displayInfo?.options && Array.isArray(displayInfo.options) && displayInfo.options.length > 0) {
        return displayInfo.options.map((opt: any, idx: number) => ({
          value: opt.side || `opt-${idx}`, // Use side as the unique value
          label: `${opt.label} (${formatOdds(opt.odds)})`,
          side: opt.side,
          teamId: opt.side, // Use side as teamId for custom props
        }));
      }
      // Legacy 2-sided prop
      const labelOne = typeof displayInfo?.sideLabelOne === 'string' ? displayInfo.sideLabelOne : 'Side 1';
      const labelTwo = typeof displayInfo?.sideLabelTwo === 'string' ? displayInfo.sideLabelTwo : 'Side 2';
      return [
        {
          value: game.homeTeam.id,
          label: `${labelOne} (${formatOdds(oddsOne)})`,
          side: 'one',
          teamId: game.homeTeam.id,
        },
        {
          value: game.awayTeam.id,
          label: `${labelTwo} (${formatOdds(oddsTwo)})`,
          side: 'two',
          teamId: game.awayTeam.id,
        },
      ];
    }

    // Moneyline or Spread
    const spreadValue = typeof displayInfo?.spread === 'number' ? displayInfo.spread : null;
    const isSpread = betType === 'spread';
    
    return [
      {
        value: game.awayTeam.id,
        label: `${game.awayTeam.abbreviation} ${isSpread ? formatSpread(spreadValue ? -spreadValue : null) : ''} (${formatOdds(oddsTwo ?? game.awayOdds)})`,
        side: 'two',
        teamId: game.awayTeam.id,
      },
      {
        value: game.homeTeam.id,
        label: `${game.homeTeam.abbreviation} ${isSpread ? formatSpread(spreadValue) : ''} (${formatOdds(oddsOne ?? game.homeOdds)})`,
        side: 'one',
        teamId: game.homeTeam.id,
      },
    ];
  }, [selectedBet, selectedGameGroup]);

  // Find existing pick for the selected bet
  const existingPick = useMemo(() => {
    if (!selectedUserPicks || !selectedBet || !selectedGameGroup) return null;
    const betKey = buildBetKey(selectedGameGroup.game.id, selectedBet);
    
    return selectedUserPicks.find((item: any) => {
      const pick = item.picks || item;
      const pickBetKey = pick.leagueWeekBetId ?? `${pick.gameId}-${pick.betType ?? 'moneyline'}`;
      return pickBetKey === betKey;
    });
  }, [selectedUserPicks, selectedBet, selectedGameGroup]);

  const existingPickSelectionValue = useMemo(() => {
    if (!existingPick || !selectedBet) return '';
    if (selectedBet.betType === 'total_tiebreaker') return 'tiebreaker';

    const pick = existingPick.picks || existingPick;
    const option = sideOptions.find((candidate) => {
      if (pick.side && candidate.side) return candidate.side === pick.side;
      return candidate.value === pick.selectedTeamId;
    });
    return option?.value ?? '';
  }, [existingPick, selectedBet, sideOptions]);

  useEffect(() => {
    if (selectedBet?.betType !== 'total_tiebreaker') return;
    const pick = existingPick?.picks ?? existingPick;
    const guess =
      pick && typeof pick.details?.tiebreakerGuess === 'number'
        ? String(pick.details.tiebreakerGuess)
        : '';
    setTiebreakerGuessInput(guess);
  }, [existingPick, selectedBet?.betType]);

  // Submit admin pick mutation
  const submitAdminPickMutation = useMutation({
    mutationFn: async (data: any) => {
      return trpcClient.picks.adminOverride.mutate({
        leagueId,
        userId: data.userId,
        gameId: data.gameId,
        selectedTeamId: data.selectedTeamId,
        side: data.side,
        ...(data.seasonId ? { seasonId: data.seasonId } : {}),
        ...(data.leagueWeekBetId ? { leagueWeekBetId: data.leagueWeekBetId } : {}),
        ...(data.betType ? { betType: data.betType } : {}),
        ...(data.tiebreakerGuess !== undefined ? { tiebreakerGuess: data.tiebreakerGuess } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.picks.usersWithoutPicks.queryFilter({ leagueId }));
      queryClient.invalidateQueries(trpc.picks.userPicks.queryFilter({ leagueId }));
      queryClient.invalidateQueries(trpc.picks.list.queryFilter()); // Refresh general picks if needed
      
      toast.show({
        type: 'success',
        text1: 'Pick submitted',
        text2: 'Admin pick has been submitted successfully',
      });
      
      // Optional: Clear selection or keep it to edit another? 
      // Keeping selections is usually better for bulk edits, but maybe clear side.
      setSelectedSideValue(''); 
    },
    onError: (error: any, variables: any) => {
      setSelectedSideValue(variables.previousSideValue ?? '');
      if (variables.previousTiebreakerGuess !== undefined) {
        setTiebreakerGuessInput(variables.previousTiebreakerGuess);
      }

      if (error?.message === PICK_LOCKED_ERROR) {
        toast.show({
          type: 'error',
          text1: 'Picks are locked',
          text2: 'The game has finished, so the existing pick was not changed.',
        });
        return;
      }

      toast.show({
        type: 'error',
        text1: 'Error',
        text2: error.message || 'Failed to submit admin pick',
      });
    },
  });

  const submitRandomPicksMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUserId) {
        throw new Error('Select a user before submitting random picks.');
      }
      if (!selectedWeekId) {
        throw new Error('No active round is selected.');
      }

      return trpcClient.picks.randomPicks.mutate({
        leagueId,
        userId: selectedUserId,
        weekId: selectedWeekId,
        ...(seasonId ? { seasonId } : {}),
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(trpc.picks.usersWithoutPicks.queryFilter({ leagueId }));
      queryClient.invalidateQueries(trpc.picks.userPicks.queryFilter({ leagueId }));
      queryClient.invalidateQueries(trpc.picks.list.queryFilter());

      const assignedCount = typeof data?.assignedCount === 'number' ? data.assignedCount : 0;
      const errorCount = typeof data?.errorCount === 'number' ? data.errorCount : 0;
      toast.show({
        type: errorCount > 0 ? 'error' : 'success',
        text1: errorCount > 0 ? 'Some picks failed' : 'Random picks submitted',
        text2:
          assignedCount > 0
            ? `Submitted ${assignedCount} random pick${assignedCount === 1 ? '' : 's'} for this round.`
            : data?.message ?? 'No missing picks to submit.',
      });
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        text1: 'Random picks failed',
        text2: error.message || 'Failed to submit random picks',
      });
    },
  });

  const handleSubmitPick = () => {
    if (!selectedUserId || !selectedGameId || !selectedBet || !selectedSideValue) {
      toast.show({
        type: 'error',
        text1: 'Missing information',
        text2: 'Please select a user, game, bet, and side',
      });
      return;
    }

    const currentPick = existingPick?.picks || existingPick;
    const previousSideValue = existingPickSelectionValue;
    const previousTiebreakerGuess =
      currentPick && typeof currentPick.details?.tiebreakerGuess === 'number'
        ? String(currentPick.details.tiebreakerGuess)
        : '';

    if (selectedBet.betType === 'total_tiebreaker') {
      const trimmed = tiebreakerGuessInput.trim();
      const guess = Number.parseFloat(trimmed);
      if (!trimmed || !Number.isFinite(guess)) {
        toast.show({
          type: 'error',
          text1: 'Invalid tiebreaker',
          text2: 'Enter a valid number for the tiebreaker guess.',
        });
        return;
      }
    }

    const selectedOption = sideOptions.find(opt => opt.value === selectedSideValue);
    if (!selectedOption) return;

    submitAdminPickMutation.mutate({
      userId: selectedUserId,
      gameId: selectedGameId,
      selectedTeamId: selectedOption.teamId, // Pass teamId (which might be side string for props)
      side: selectedOption.side, // Explicitly pass side
      seasonId,
      leagueWeekBetId: selectedBet.leagueWeekBetId,
      betType: selectedBet.betType,
      tiebreakerGuess:
        selectedBet.betType === 'total_tiebreaker' ? Number.parseFloat(tiebreakerGuessInput.trim()) : undefined,
      previousSideValue,
      previousTiebreakerGuess,
    });
  };

  // Pre-select missing game if invoked from the "Missing Picks" list
  const handleSelectMissing = (userId: string, gameId: string) => {
    setSelectedUserId(userId);
    setSelectedGameId(gameId);
    // Try to select the first bet (usually Moneyline)
    const gameGroup = leagueWeekGames?.games.find(g => g.game.id === gameId);
    if (gameGroup && gameGroup.bets.length > 0) {
        const firstBet = gameGroup.bets[0]!;
        setSelectedBetId(buildBetKey(gameId, firstBet));
    }
  };

  // ... (Recap logic remains mostly same)
  async function copyRecapToClipboard(promptText?: string, successMessage?: string) {
    const textToCopy = promptText ?? recapPrompt;
    if (!textToCopy) {
      toast.show({
        type: 'info',
        text1: 'Generate prompt first',
        text2: 'Create the recap prompt before copying it.',
      });
      return;
    }
    try {
      await Clipboard.setStringAsync(textToCopy);
      setRecapCopied(true);
      toast.show({
        type: 'success',
        text1: 'Copied!',
        text2: successMessage || 'Recap prompt copied to clipboard',
      });
      setTimeout(() => setRecapCopied(false), 2000);
    } catch (error) {
      clientLogger.error('Failed to copy the recap prompt to clipboard', { error });
      toast.show({
        type: 'error',
        text1: 'Failed to copy',
        text2: 'Please manually copy the text',
      });
    }
  }

  const generateRecapPromptMutation = useMutation({
    mutationFn: async ({
      recapTypeValue,
      recapToneValue,
      weekId,
      style,
    }: {
      recapTypeValue: 'season' | 'weekly';
      recapToneValue: 'fun' | 'competitive' | 'chaotic' | 'professional';
      weekId?: string;
      style?: 'recap' | 'summary';
    }) => {
      const weekNumber =
        recapTypeValue === 'weekly' && weekId ? Number.parseInt(weekId.split('-').pop() ?? '', 10) : undefined;
      return trpcClient.picks.recap.query({
        leagueId,
        recapType: recapTypeValue,
        tone: recapToneValue,
        ...(style ? { style } : {}),
        ...(seasonId ? { seasonId } : {}),
        ...(Number.isInteger(weekNumber) ? { weekNumber } : {}),
      });
    },
    onSuccess: (data: { prompt?: string }) => {
      if (!data?.prompt) {
        toast.show({
          type: 'error',
          text1: 'Missing prompt',
          text2: 'No recap prompt was returned.',
        });
        return;
      }
      setRecapPrompt(data.prompt);
      setRecapDialogOpen(true);
      copyRecapToClipboard(data.prompt, 'AI recap prompt generated and copied to clipboard');
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: error.message || 'Failed to generate recap prompt',
      });
    },
  });

  const handleGenerateRecapPrompt = () => {
     // ... (Keep existing logic)
     if (!seasonId) {
        toast.show({
          type: 'error',
          text1: 'Season not ready',
          text2: 'Please wait for the season data to load.',
        });
        return;
      }
      // ...
      const payload: any = {
        recapTypeValue: recapType,
        recapToneValue: recapTone,
      };
  
      if (recapType === 'weekly' && recapWeekId) {
        payload.weekId = recapWeekId;
        payload.style = 'summary';
      }
  
      generateRecapPromptMutation.mutate(payload);
  };

  const getCurrentPickLabel = () => {
    if (!existingPick) return null;
    const pick = existingPick.picks || existingPick;
    
    // Find the option in sideOptions that matches
    const option = sideOptions.find(opt => {
        // Match by side if available, otherwise by teamId (value)
        if (pick.side && opt.side) return opt.side === pick.side;
        return opt.value === pick.selectedTeamId;
    });

    if (option) return option.label;
    
    // Fallback if option not found (shouldn't happen often)
    return pick.side ? `Side: ${pick.side}` : 'Unknown Selection';
  };

  if (membersLoading || gamesLoading) {
    return (
      <Card>
        <CardContent style={loadingContentStyle}>
          <ActivityIndicator size="large" color={colors.text.primary} />
          <Text style={styles.loadingText}>
            Loading admin data...
          </Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Card>
        <CardHeader>
          <View style={styles.cardTitleContainer}>
            <Shield size={20} color={colors.text.primary} />
            <CardTitle>
              <Text style={styles.cardTitle}>Admin Pick Management</Text>
            </CardTitle>
          </View>
        </CardHeader>
        <CardContent>
          {/* AI Recap Generator */}
          <View style={styles.aiSection}>
            <View style={styles.aiHeader}>
              <View style={styles.aiTitleRow}>
                <Sparkles size={20} color={colors.text.primary} />
                <Text style={styles.aiTitle}>AI Recap Generator</Text>
              </View>
            </View>
             {/* ... Recap Selectors ... */}
             <View style={styles.recapSelectors}>
              <View style={styles.recapSelector}>
                <Text style={styles.selectorLabel}>Recap Type</Text>
                <AppSelect
                  value={recapType}
                  onChange={(value) =>
                    setRecapType(value === 'weekly' ? 'weekly' : 'season')
                  }
                  options={[
                    { value: 'season', label: 'League Recap' },
                    { value: 'weekly', label: 'Weekly Recap' },
                  ]}
                  placeholder="Choose recap type..."
                />
              </View>
              <View style={styles.recapSelector}>
                <Text style={styles.selectorLabel}>Tone</Text>
                <AppSelect
                  value={recapTone}
                  onChange={(value) => setRecapTone(value as any)}
                  options={[
                    { value: 'fun', label: 'Fun' },
                    { value: 'competitive', label: 'Competitive' },
                    { value: 'chaotic', label: 'Chaotic' },
                    { value: 'professional', label: 'Professional' },
                  ]}
                  placeholder="Choose tone..."
                />
              </View>
            </View>

            {recapType === 'weekly' && (
              <View style={styles.weekSelector}>
                <Text style={styles.selectorLabel}>Select Week</Text>
                <AppSelect
                  value={recapWeekId}
                  onChange={setRecapWeekId}
                  options={seasonWeekOptions.map((week) => ({
                    value: week.id,
                    label: week.displayLabel || week.label,
                  }))}
                  placeholder="Choose a week..."
                />
              </View>
            )}

            <Button
              onPress={handleGenerateRecapPrompt}
              disabled={
                generateRecapPromptMutation.isPending ||
                (recapType === 'weekly' && !recapWeekId)
              }
              style={[
                styles.generateButton,
                generateRecapPromptMutation.isPending ||
                (recapType === 'weekly' && !recapWeekId)
                  ? styles.generateButtonDisabled
                  : null,
              ]}
            >
              <View style={styles.buttonContent}>
                <Sparkles size={16} color={colors.text.primary} />
                <Text style={styles.buttonText}>
                  {generateRecapPromptMutation.isPending ? 'Generating...' : 'Generate AI Recap'}
                </Text>
              </View>
            </Button>
          </View>

          <View style={styles.notificationSection}>
            <View style={styles.notificationHeader}>
              <View style={styles.sectionHeaderLeft}>
                <Bell size={16} color={colors.text.primary} />
                <Text style={styles.sectionTitle}>Push Notifications</Text>
              </View>
              <Badge variant="outline">
                <Text>{notificationTarget === 'missing' ? 'Targeted' : 'All members'}</Text>
              </Badge>
            </View>

            <View style={styles.formFields}>
              <View style={styles.formField}>
                <Text style={styles.fieldLabel}>Recipients</Text>
                <AppSelect
                  value={notificationTarget}
                  onChange={(value) =>
                    setNotificationTarget(value === 'missing' ? 'missing' : 'all')
                  }
                  options={notificationRecipientOptions}
                  placeholder="Choose recipients..."
                />
              </View>

              <View style={styles.formField}>
                <Text style={styles.fieldLabel}>Message preset</Text>
                <AppSelect
                  value={notificationPreset}
                  onChange={handleNotificationPresetChange}
                  options={notificationPresetOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  placeholder="Choose a preset..."
                />
              </View>

              <View style={styles.formField}>
                <View style={styles.messageHeader}>
                  <Text style={styles.fieldLabel}>Title</Text>
                  <Text style={styles.messageCounter}>
                    {notificationTitle.length}/{notificationTitleLimit}
                  </Text>
                </View>
                <Input
                  value={notificationTitle}
                  onChangeText={setNotificationTitle}
                  placeholder="League Update"
                  maxLength={notificationTitleLimit}
                />
              </View>

              <View style={styles.formField}>
                <View style={styles.messageHeader}>
                  <Text style={styles.fieldLabel}>Message</Text>
                  <Text style={styles.messageCounter}>
                    {notificationMessage.length}/{notificationMessageLimit}
                  </Text>
                </View>
                <Textarea
                  value={notificationMessage}
                  onChangeText={setNotificationMessage}
                  placeholder="Write your message..."
                  maxLength={notificationMessageLimit}
                />
              </View>
            </View>

            <Text style={styles.helperText}>
              {notificationTarget === 'missing'
                ? `Sends to ${missingPicksCount} member${missingPicksCount === 1 ? '' : 's'} missing picks for the selected week.`
                : 'Sends to all league members with push notifications enabled.'}
            </Text>

            <Button
              onPress={() => sendNotificationMutation.mutate()}
              disabled={
                !notificationMessage.trim() ||
                (notificationTarget === 'missing' && !selectedWeekId) ||
                sendNotificationMutation.isPending
              }
              style={styles.sendNotificationButton}
            >
              <View style={styles.buttonContent}>
                <Bell size={16} color={colors.text.primary} />
                <Text style={styles.buttonText}>
                  {sendNotificationMutation.isPending ? 'Sending...' : 'Send Notification'}
                </Text>
              </View>
            </Button>
          </View>

          {squaresState?.enabled && (
            <View style={styles.squaresSection}>
              <View style={styles.notificationHeader}>
                <View style={styles.sectionHeaderLeft}>
                  <Shield size={16} color={colors.success[700]} />
                  <Text style={styles.sectionTitle}>Super Bowl Squares</Text>
                </View>
                <Badge variant="outline">
                  <Text>{squaresState.locked ? 'Locked' : 'Open'}</Text>
                </Badge>
              </View>
              <Text style={styles.helperText}>
                {missingSquaresCount > 0
                  ? `${missingSquaresCount} member${missingSquaresCount === 1 ? '' : 's'} still need a square pick.`
                  : 'All league members already have a square pick.'}
              </Text>
              <Button
                onPress={() => autoAssignSquaresMutation.mutate()}
                disabled={
                  squaresState.locked ||
                  missingSquaresCount <= 0 ||
                  autoAssignSquaresMutation.isPending
                }
                style={styles.assignSquaresButton}
              >
                <Text style={styles.buttonText}>
                  {autoAssignSquaresMutation.isPending
                    ? 'Assigning...'
                    : 'Auto-assign Missing Squares'}
                </Text>
              </Button>

              <View style={styles.squareOverrideSection}>
                <Text style={styles.fieldLabel}>Manual Square Override</Text>
                <Text style={styles.helperText}>
                  Force-assign a specific square to a selected member.
                </Text>
                <View style={styles.formFields}>
                  <View style={styles.formField}>
                    <Text style={styles.fieldLabel}>Member</Text>
                    <AppSelect
                      value={overrideSquareUserId}
                      onChange={setOverrideSquareUserId}
                      options={squareOverrideMemberOptions}
                      placeholder="Select member"
                    />
                  </View>
                  <View style={styles.squareCoordinateRow}>
                    <View style={[styles.formField, styles.squareCoordinateField]}>
                      <Text style={styles.fieldLabel}>Row</Text>
                      <AppSelect
                        value={overrideSquareRow}
                        onChange={setOverrideSquareRow}
                        options={squareCoordinateOptions}
                        placeholder="Row"
                      />
                    </View>
                    <View style={[styles.formField, styles.squareCoordinateField]}>
                      <Text style={styles.fieldLabel}>Column</Text>
                      <AppSelect
                        value={overrideSquareCol}
                        onChange={setOverrideSquareCol}
                        options={squareCoordinateOptions}
                        placeholder="Column"
                      />
                    </View>
                  </View>
                </View>
                <Text style={styles.squareOwnerText}>
                  {selectedSquareOwner
                    ? `Current owner: ${selectedSquareOwner}`
                    : 'Current owner: Unclaimed'}
                </Text>
                <Button
                  onPress={() => overrideSquareMutation.mutate()}
                  disabled={
                    squaresState.locked ||
                    !overrideSquareUserId ||
                    overrideSquareMutation.isPending
                  }
                  style={styles.overrideSquaresButton}
                >
                  <Text style={styles.buttonText}>
                    {overrideSquareMutation.isPending ? 'Overriding...' : 'Override Square'}
                  </Text>
                </Button>
              </View>
            </View>
          )}

          {/* Missing Picks Section (Collapsible) */}
          {usersWithoutPicks && usersWithoutPicks.length > 0 && (
            <View style={styles.sectionContainer}>
              <TouchableOpacity 
                style={styles.sectionHeader} 
                onPress={() => setShowMissingPicks(!showMissingPicks)}
              >
                <View style={styles.sectionHeaderLeft}>
                    <AlertTriangle size={16} color={colors.warning[600]} />
                    <Text style={styles.sectionTitle}>
                        Users Missing Picks ({usersWithoutPicks.length})
                    </Text>
                </View>
                {showMissingPicks ? (
                  <ChevronUp size={16} color={colors.text.secondary} />
                ) : (
                  <ChevronDown size={16} color={colors.text.secondary} />
                )}
              </TouchableOpacity>
              
              {showMissingPicks && (
                <View style={styles.usersList}>
                  {usersWithoutPicks.map((userInfo) => (
                    <TouchableOpacity 
                        key={userInfo.userId} 
                        style={styles.missingUserRow}
                        onPress={() => {
                            if (userInfo.missingGames.length > 0) {
                                handleSelectMissing(userInfo.userId, userInfo.missingGames[0]!);
                            }
                        }}
                    >
                      <View style={styles.userInfoSmall}>
                         <Avatar style={styles.avatarSmall}>
                            <Text style={styles.avatarTextSmall}>
                              {userInfo.user.firstName?.charAt(0)}
                              {userInfo.user.lastName?.charAt(0)}
                            </Text>
                          </Avatar>
                          <Text style={styles.userNameSmall}>
                            {userInfo.user.firstName} {userInfo.user.lastName}
                          </Text>
                      </View>
                      <Badge variant="destructive">
                        <Text>{userInfo.missingGames.length} missing</Text>
                      </Badge>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          )}

          {/* Main Edit Pick Form */}
          <View style={styles.adminFormSection}>
            <View style={styles.adminFormHeader}>
              <Crown size={16} color={colors.warning[600]} />
              <Text style={styles.adminFormTitle}>Make / Edit Pick</Text>
            </View>

            <View style={styles.formFields}>
              {/* User Selection */}
              <View style={styles.formField}>
                <Text style={styles.fieldLabel}>User</Text>
                <AppSelect
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                  options={members.map((m) => ({
                    value: m.users.id,
                    label: `${m.users.firstName} ${m.users.lastName}`,
                  }))}
                  placeholder="Select a user..."
                />
              </View>

              <View style={styles.randomPicksBlock}>
                <Text style={styles.helperText}>
                  Fill every missing pick for the selected user in this round with random sides.
                </Text>
                <Button
                  onPress={() => submitRandomPicksMutation.mutate()}
                  disabled={!selectedUserId || !selectedWeekId || !seasonId || submitRandomPicksMutation.isPending}
                  style={styles.randomPicksButton}
                >
                  <View style={styles.buttonContent}>
                    <Sparkles size={16} color={colors.text.primary} />
                    <Text style={styles.buttonText}>
                      {submitRandomPicksMutation.isPending ? 'Submitting...' : 'Submit Random Round Picks'}
                    </Text>
                  </View>
                </Button>
              </View>

              {/* Game Selection */}
              <View style={styles.formField}>
                <Text style={styles.fieldLabel}>Game</Text>
                <AppSelect
                  value={selectedGameId}
                  onChange={(val) => {
                      setSelectedGameId(val);
                      setSelectedBetId(''); // Reset bet when game changes
                      setSelectedSideValue('');
                      setTiebreakerGuessInput('');
                  }}
                  options={(leagueWeekGames?.games || []).map((g) => ({
                    value: g.game.id,
                    label: `${g.game.awayTeam.abbreviation} @ ${g.game.homeTeam.abbreviation}`,
                  }))}
                  placeholder="Select a game..."
                  disabled={!selectedUserId}
                />
              </View>

              {/* Bet Selection */}
              <View style={styles.formField}>
                <Text style={styles.fieldLabel}>Bet (Market)</Text>
                <AppSelect
                  value={selectedBetId}
                  onChange={(val) => {
                      setSelectedBetId(val);
                      setSelectedSideValue('');
                      setTiebreakerGuessInput('');
                  }}
                  options={availableBets.map((bet) => ({
                    value: buildBetKey(selectedGameId, bet),
                    label: buildBetLabel(bet),
                  }))}
                  placeholder={availableBets.length > 0 ? "Select a bet..." : "No bets available"}
                  disabled={!selectedGameId || availableBets.length === 0}
                />
              </View>

              {/* Side Selection */}
              {selectedBet?.betType === 'total_tiebreaker' ? (
                <View style={styles.formField}>
                  <Text style={styles.fieldLabel}>Tiebreaker Guess</Text>
                  <Input
                    value={tiebreakerGuessInput}
                    onChangeText={setTiebreakerGuessInput}
                    keyboardType="decimal-pad"
                    placeholder="Enter total points"
                    editable={!!selectedBetId}
                  />
                </View>
              ) : (
                <View style={styles.formField}>
                  <Text style={styles.fieldLabel}>Pick Side</Text>
                  <AppSelect
                    value={selectedSideValue}
                    onChange={setSelectedSideValue}
                    options={sideOptions}
                    placeholder="Select side..."
                    disabled={!selectedBetId}
                  />
                </View>
              )}

              {/* Current Pick Display */}
              {userPicksLoading ? (
                  <ActivityIndicator size="small" />
              ) : existingPick ? (
                  <View style={styles.currentPickContainer}>
                      <Text style={styles.currentPickLabel}>Current Pick:</Text>
                      <Text style={styles.currentPickValue}>{getCurrentPickLabel()}</Text>
                  </View>
              ) : selectedBetId ? (
                  <View style={styles.currentPickContainer}>
                       <Text style={styles.currentPickLabel}>Current Pick:</Text>
                       <Text style={styles.noPickValue}>No pick made</Text>
                  </View>
              ) : null}

            </View>

            <Button
              onPress={handleSubmitPick}
              disabled={
                !selectedUserId ||
                !selectedGameId ||
                !selectedBetId ||
                !selectedSideValue ||
                (selectedBet?.betType === 'total_tiebreaker' && !tiebreakerGuessInput.trim()) ||
                submitAdminPickMutation.isPending
              }
              style={styles.submitButton}
            >
              <Text style={styles.buttonText}>
                {submitAdminPickMutation.isPending ? 'Submitting...' : existingPick ? 'Update Pick' : 'Submit Pick'}
              </Text>
            </Button>
            
            {Boolean(selectedUserId && selectedGameGroup && selectedSideValue) && (
                  <View style={styles.confirmationAlert}>
                    <AlertTriangle size={16} color={colors.warning[600]} />
                    <Text style={styles.confirmationText}>
                      {existingPick ? 'Updating' : 'Submitting'} pick for{' '}
                      <Text style={styles.boldText}>
                        {members.find(m => m.users.id === selectedUserId)?.users.firstName}
                      </Text>{' '}
                      to{' '}
                      <Text style={styles.boldText}>
                        {selectedBet?.betType === 'total_tiebreaker'
                          ? `Tiebreaker: ${tiebreakerGuessInput || '—'}`
                          : sideOptions.find(o => o.value === selectedSideValue)?.label}
                      </Text>
                    </Text>
                  </View>
            )}
          </View>
        </CardContent>
      </Card>

      {/* Recap Modal (Keep existing) */}
      <Modal
            visible={recapDialogOpen}
            transparent
            animationType="slide"
            onRequestClose={() => setRecapDialogOpen(false)}
          >
            <View style={styles.modalOverlay}>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <View style={styles.modalTitleRow}>
                    <Sparkles size={20} color={colors.text.primary} />
                    <Text style={styles.modalTitle}>AI Recap Prompt</Text>
                  </View>
                  <TouchableOpacity onPress={() => setRecapDialogOpen(false)}>
                    <X size={24} color={colors.text.secondary} />
                  </TouchableOpacity>
                </View>
                <Text style={styles.modalDescription}>
                  Copy this prompt and paste it into your favorite LLM.
                </Text>
                <ScrollView style={styles.promptScrollView}>
                  <TextInput
                    value={recapPrompt}
                    multiline
                    editable={false}
                    style={styles.promptInput}
                  />
                </ScrollView>
                <Button onPress={() => copyRecapToClipboard()} style={styles.copyButton}>
                  <View style={styles.buttonContent}>
                    {recapCopied ? (
                      <>
                        <Check size={16} color={colors.text.primary} />
                        <Text style={styles.buttonText}>Copied!</Text>
                      </>
                    ) : (
                      <>
                        <Copy size={16} color={colors.text.primary} />
                        <Text style={styles.buttonText}>Copy to Clipboard</Text>
                      </>
                    )}
                  </View>
                </Button>
              </View>
            </View>
          </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create(() => ({
  adminFormHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  adminFormSection: {
    backgroundColor: colors.ink.paper,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 2,
    marginTop: 0,
    padding: 16,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  adminFormTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 16,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  aiHeader: {
    marginBottom: 12,
  },
  aiSection: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 2,
    marginBottom: 24,
    padding: 16,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  aiTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 16,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  aiTitleRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  assignSquaresButton: {
    backgroundColor: colors.success[100],
    borderColor: colors.ink.press,
    borderWidth: 2,
    width: '100%',
  },
  avatarSmall: {
    height: 28,
    width: 28,
  },
  avatarTextSmall: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '600',
  },
  boldText: {
    fontWeight: '600',
  },
  buttonContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
  },
  buttonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  cardTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  cardTitleContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  confirmationAlert: {
    alignItems: 'flex-start',
    backgroundColor: colors.warning[50],
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    flexDirection: 'row',
    gap: 8,
    padding: 12,
  },
  confirmationText: {
    color: colors.warning[900],
    flex: 1,
    fontSize: 14,
  },
  container: {
    flex: 1,
  },
  copyButton: {
    width: '100%',
  },
  currentPickContainer: {
      alignItems: 'center',
      backgroundColor: colors.ink.acid,
      borderColor: colors.ink.press,
      borderRadius: 0,
      borderWidth: 2,
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: 12,
  },
  currentPickLabel: {
      color: colors.text.primary,
      fontFamily: typography.fontFamily.mono,
      fontSize: 14,
      fontWeight: '600',
  },
  currentPickValue: {
      color: colors.text.primary,
      fontFamily: typography.fontFamily.bodyMedium,
      fontSize: 14,
      fontWeight: '700',
  },
  fieldLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  formField: {
    gap: 8,
  },
  formFields: {
    gap: 16,
    marginBottom: 16,
  },
  generateButton: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    elevation: 2,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    width: '100%',
  },
  generateButtonDisabled: {
    backgroundColor: colors.neutral[200],
    shadowOpacity: 0,
  },
  helperText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 13,
    marginBottom: 12,
  },
  loadingText: {
    color: colors.text.secondary,
    marginTop: 12,
    textAlign: 'center',
  },
  messageCounter: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
  },
  messageHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  missingUserRow: {
      alignItems: 'center',
      backgroundColor: colors.ink.paperDark,
      borderColor: colors.ink.press,
      borderRadius: 0,
      borderWidth: 2,
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: 12,
  },
  modalContent: {
    backgroundColor: colors.ink.paper,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 6,
    maxHeight: '80%',
    padding: 20,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 14, height: 14 },
    shadowOpacity: 1,
    shadowRadius: 0,
    width: '100%',
  },
  modalDescription: {
    color: colors.text.secondary,
    fontSize: 14,
    marginBottom: 16,
  },
  modalHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  modalOverlay: {
    alignItems: 'center',
    backgroundColor: modalOverlayColor,
    flex: 1,
    justifyContent: 'center',
    padding: 16,
  },
  modalTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  modalTitleRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  noPickValue: {
      color: colors.text.secondary,
      fontSize: 14,
      fontStyle: 'italic',
  },
  notificationHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  notificationSection: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    marginBottom: 24,
    padding: 16,
  },
  overrideSquaresButton: {
    backgroundColor: colors.success[100],
    borderColor: colors.ink.press,
    borderWidth: 2,
    width: '100%',
  },
  promptInput: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    minHeight: 300,
    padding: 12,
    textAlignVertical: 'top',
  },
  promptScrollView: {
    marginBottom: 16,
    maxHeight: 400,
  },
  randomPicksBlock: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    gap: 8,
    padding: 12,
  },
  randomPicksButton: {
    backgroundColor: colors.warning[100],
    borderColor: colors.ink.press,
    borderWidth: 2,
    width: '100%',
  },
  recapSelector: {
    width: '100%',
  },
  recapSelectors: {
    gap: 12,
    marginBottom: 16,
  },
  sectionContainer: {
    marginBottom: 24,
  },
  sectionHeader: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 8,
  },
  sectionHeaderLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    gap: 8,
  },
  sectionTitle: {
    color: colors.text.primary,
    flexShrink: 1,
    fontFamily: typography.fontFamily.display,
    fontSize: 16,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  selectorLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  sendNotificationButton: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderWidth: 2,
    width: '100%',
  },
  squareCoordinateField: {
    flex: 1,
  },
  squareCoordinateRow: {
    flexDirection: 'row',
    gap: 12,
  },
  squareOverrideSection: {
    borderTopColor: colors.ink.press,
    borderTopWidth: 2,
    gap: 8,
    marginTop: 16,
    paddingTop: 16,
  },
  squareOwnerText: {
    color: colors.text.secondary,
    fontSize: 13,
    marginBottom: 8,
  },
  squaresSection: {
    backgroundColor: colors.success[50],
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    marginBottom: 24,
    padding: 16,
  },
  submitButton: {
    marginBottom: 16,
    width: '100%',
  },
  userInfoSmall: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 8,
  },
  userNameSmall: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '500',
  },
  usersList: {
    gap: 8,
    marginTop: 8,
  },
  weekSelector: {
    marginBottom: 16,
  },
}));
